/**
 * Israeli IBAN parsing and bank-code lookup.
 *
 * Israeli IBAN layout (23 chars):
 *   IL | 2 check digits | 3-digit bank code | 3-digit branch code | 13-digit account number
 *   e.g. IL26 0108 0800 0000 9991 123
 *        └┬┘ └┬┘ └─┬─┘ └────────┬────────┘
 *        IL  26  010 808  0000009991123
 */

/** Map of 2-digit Israeli bank code → Hebrew bank name (source: Bank of Israel) */
export const ISRAELI_BANKS: Record<string, string> = {
  '04': 'בנק יהב',
  '09': 'בנק הדואר',
  '10': 'בנק לאומי לישראל',
  '11': 'בנק דיסקונט לישראל',
  '12': 'בנק הפועלים',
  '13': 'בנק אגוד לישראל',
  '14': 'בנק אוצר החייל',
  '17': 'בנק מרכנתיל דיסקונט',
  '18': 'וואן זירו הבנק הדיגיטלי',
  '20': 'בנק מזרחי טפחות',
  '22': 'Citibank',
  '23': 'HSBC',
  '26': 'יובנק',
  '31': 'הבנק הבינלאומי הראשון',
  '34': 'בנק ערבי ישראלי',
  '46': 'בנק מסד',
  '52': 'בנק פועלי אגודת ישראל',
  '54': 'בנק ירושלים',
  '68': 'בנק קונביננס (מימון ישיר)',
  '99': 'בנק ישראל'
};

/** Parsed components of an Israeli IBAN */
export interface IsraeliIbanParts {
  /** Bank code with leading zeros stripped, e.g. "10" */
  bankCode: string;
  /** Branch code with leading zeros stripped, e.g. "808" */
  branchCode: string;
  /** Account number with leading zeros stripped, e.g. "9991123" */
  accountNumber: string;
  /** IBAN grouped in blocks of 4 for display, e.g. "IL26 0108 0800 0000 9991 123" */
  formatted: string;
}

const IL_IBAN_LENGTH = 23;

/**
 * Parse an Israeli IBAN into its bank code, branch code and account number.
 *
 * Accepts the IBAN with or without spaces, in any case. Returns null when the
 * value is not a structurally valid Israeli IBAN.
 *
 * @example
 * parseIsraeliIban('IL26 0108 0800 0000 9991 123')
 * // { bankCode: '10', branchCode: '808', accountNumber: '9991123', formatted: 'IL26 0108 0800 0000 9991 123' }
 * parseIsraeliIban('not-an-iban') // null
 */
export function parseIsraeliIban(iban: string): IsraeliIbanParts | null {
  const normalized = iban.replace(/\s+/g, '').toUpperCase();

  if (normalized.length !== IL_IBAN_LENGTH) return null;
  if (!normalized.startsWith('IL')) return null;
  if (!/^IL\d{21}$/.test(normalized)) return null;

  const bankCode = normalized.slice(4, 7);
  const branchCode = normalized.slice(7, 10);
  const accountNumber = normalized.slice(10, 23);

  return {
    bankCode: stripLeadingZeros(bankCode),
    branchCode: stripLeadingZeros(branchCode),
    accountNumber: stripLeadingZeros(accountNumber),
    formatted: formatIban(normalized)
  };
}

/** Resolve the Hebrew bank name for a 2-digit Israeli bank code. */
export function getBankName(bankCode: string): string | undefined {
  return ISRAELI_BANKS[bankCode.padStart(2, '0')];
}

/** Group an IBAN into blocks of 4 characters for display. */
export function formatIban(iban: string): string {
  const normalized = iban.replace(/\s+/g, '').toUpperCase();
  return normalized.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Compose an Israeli IBAN from its bank code, branch code and account number.
 *
 * @example
 * composeIsraeliIban({bankCode: '10', branchCode: '808', accountNumber: '9991123'})
 * // 'IL26 0108 0800 0000 9991 123'
 */
export function composeIsraeliIban(params: {
  bankCode: string;
  branchCode: string;
  accountNumber: string;
  withSpaces?: boolean;
}): string {
  const bank = params.bankCode.replace(/\s+/g, '').padStart(3, '0');
  const branch = params.branchCode.replace(/\s+/g, '').padStart(3, '0');
  const account = params.accountNumber.replace(/\s+/g, '').padStart(13, '0');

  if (!/^\d{3}$/.test(bank)) throw new Error(`Invalid bank code: ${params.bankCode}`);
  if (!/^\d{3}$/.test(branch)) throw new Error(`Invalid branch code: ${params.branchCode}`);
  if (!/^\d{13}$/.test(account)) throw new Error(`Invalid account number: ${params.accountNumber}`);

  const bban = bank + branch + account; // 19 digits
  // ISO 7064 Mod 97-10: move IL00 to the right, replace letters with digits (I=18, L=21)
  const numericString = bban + '182100'; // IL → 1821, 00 placeholder
  const checkDigits = String(98n - (BigInt(numericString) % 97n)).padStart(2, '0');

  const iban = `IL${checkDigits}${bban}`;
  return (params.withSpaces ?? true) ? formatIban(iban) : iban;
}

/** An Israeli bank account as shown for a Masav transfer — bank / branch / account, never the IBAN. */
export interface IsraeliBankAccount {
  /** Hebrew bank name, e.g. 'בנק לאומי לישראל' */
  bankName: string;
  /** Bank code with leading zeros stripped, e.g. '10' */
  bankCode: string;
  /** Branch code with leading zeros stripped, e.g. '808' */
  branchCode: string;
  /** Account number with leading zeros stripped, e.g. '9991123' */
  accountNumber: string;
}

/** Human-readable breakdown of an Israeli bank account derived from an IBAN. */
export interface IsraeliBankAccountDisplay extends IsraeliBankAccount {
  /** IBAN formatted in blocks of 4, e.g. 'IL26 0108 0800 0000 9991 123' */
  iban: string;
}

/**
 * Parse an Israeli IBAN and resolve the Hebrew bank name in one call.
 *
 * Returns `null` when the IBAN is structurally invalid (not a valid IL IBAN)
 * or when the bank code is not recognised.
 *
 * @example
 * formatIsraeliBankAccount('IL26 0108 0800 0000 9991 123')
 * // {
 * //   bankName: 'בנק לאומי לישראל',
 * //   bankCode: '10',
 * //   branchCode: '808',
 * //   accountNumber: '9991123',
 * //   iban: 'IL26 0108 0800 0000 9991 123',
 * // }
 * formatIsraeliBankAccount('not-an-iban') // null
 */
export function formatIsraeliBankAccount(rawIban: string): IsraeliBankAccountDisplay | null {
  const parts = parseIsraeliIban(rawIban);
  if (!parts) return null;

  const bankName = getBankName(parts.bankCode);
  if (bankName === undefined) return null;

  return {
    bankName,
    bankCode: parts.bankCode,
    branchCode: parts.branchCode,
    accountNumber: parts.accountNumber,
    iban: parts.formatted
  };
}

/**
 * Formats an Israeli IBAN as its BBAN display form "bank-branch-account" (no country
 * code or check digits). Returns the input unchanged if it isn't a structurally valid
 * Israeli IBAN, so callers can pass a value of uncertain format safely.
 *
 * @example
 * formatIsraeliBban('IL26 0108 0800 0000 9991 123') // '10-808-9991123'
 * formatIsraeliBban('not-an-iban') // 'not-an-iban'
 */
export function formatIsraeliBban(iban: string): string {
  const parts = parseIsraeliIban(iban);
  return parts ? joinAccountParts(parts) : iban;
}

/**
 * Canonical "bank-branch-account" key for an Israeli account given either as an IBAN or in the
 * domestic `0012-0661-0000371925` form, so the same account compares equal across both.
 * Returns null for anything else.
 *
 * @example
 * israeliAccountKey('IL76 0205 7200 0000 0391 016') // '20-572-391016'
 * israeliAccountKey('0020-0572-0000391016') // '20-572-391016'
 */
export function israeliAccountKey(raw: string): string | null {
  const parts = parseIsraeliAccountParts(raw);
  return parts ? joinAccountParts(parts) : null;
}

/**
 * Bank / branch / account breakdown of an Israeli account given either as an IBAN or in the
 * domestic `0012-0661-0000371925` form, with the Hebrew bank name resolved. Returns null for
 * anything else, or when the bank code is not recognised.
 *
 * @example
 * parseIsraeliBankAccount('0010-0808-0000009991123')
 * // {bankName: 'בנק לאומי לישראל', bankCode: '10', branchCode: '808', accountNumber: '9991123'}
 */
export function parseIsraeliBankAccount(raw: string): IsraeliBankAccount | null {
  const parts = parseIsraeliAccountParts(raw);
  if (!parts) return null;

  const bankName = getBankName(parts.bankCode);
  if (bankName === undefined) return null;

  return {bankName, ...parts};
}

/**
 * Normalize any creditor account input to the flat {accountNumber, accountType}
 * pair expected by the provider and stored in the database.
 *
 * - `'iban'`/`'bban'` → whitespace stripped and uppercased
 * - `'bank_details'`  → composed into a compact IBAN via {@link composeIsraeliIban}, type `'iban'`
 */
export function resolveCreditorAccount(
  input:
    | {
        inputType: 'iban' | 'bban';
        accountNumber: string;
      }
    | {
        inputType: 'bank_details';
        bankCode: string;
        branchCode: string;
        accountNumber: string;
      }
): {
  accountNumber: string;
  accountType: 'iban' | 'bban';
} {
  switch (input.inputType) {
    case 'iban':
    case 'bban':
      return {
        accountNumber: input.accountNumber.replace(/\s+/g, '').toUpperCase(),
        accountType: input.inputType
      };
    case 'bank_details':
      return {
        accountNumber: composeIsraeliIban({
          bankCode: input.bankCode,
          branchCode: input.branchCode,
          accountNumber: input.accountNumber,
          withSpaces: false
        }),
        accountType: 'iban'
      };
  }
}

function isDigitsUpTo(value: string, maxLength: number): boolean {
  return new RegExp(`^\\d{1,${maxLength}}$`).test(value.trim());
}

/** Bank code as used by {@link composeIsraeliIban} — up to 3 digits before zero-padding. */
export function isValidIsraeliBankCode(bankCode: string): boolean {
  return isDigitsUpTo(bankCode, 3);
}

/** Branch code as used by {@link composeIsraeliIban} — up to 3 digits before zero-padding. */
export function isValidIsraeliBranchCode(branchCode: string): boolean {
  return isDigitsUpTo(branchCode, 3);
}

/** Account number as used by {@link composeIsraeliIban} — up to 13 digits before zero-padding. */
export function isValidIsraeliBankAccountNumber(accountNumber: string): boolean {
  return isDigitsUpTo(accountNumber, 13);
}

/**
 * A structurally valid Israeli IBAN whose ISO 7064 mod-97 checksum also holds — so a single
 * mistyped digit is rejected, not just a wrong length or prefix.
 *
 * @example
 * isValidIsraeliIban('IL26 0108 0800 0000 9991 123') // true
 * isValidIsraeliIban('IL27 0108 0800 0000 9991 123') // false — check digits don't match
 */
export function isValidIsraeliIban(iban: string): boolean {
  const parts = parseIsraeliIban(iban);
  if (!parts) return false;
  // Recomposing from the parts recomputes the check digits — they must match the given ones.
  const {bankCode, branchCode, accountNumber} = parts;
  return (
    composeIsraeliIban({bankCode, branchCode, accountNumber, withSpaces: false}) ===
    iban.replace(/\s+/g, '').toUpperCase()
  );
}

/** Israeli BBAN — 5 to 15 digits (bank + branch + account, no country code or check digits). */
export function isValidIsraeliBban(accountNumber: string): boolean {
  return /^\d{5,15}$/.test(accountNumber.replace(/\s/g, ''));
}

function stripLeadingZeros(value: string): string {
  const stripped = value.replace(/^0+/, '');
  return stripped === '' ? '0' : stripped;
}

function parseIsraeliAccountParts(
  raw: string
): Pick<IsraeliBankAccount, 'bankCode' | 'branchCode' | 'accountNumber'> | null {
  const iban = parseIsraeliIban(raw);
  if (iban) {
    const {bankCode, branchCode, accountNumber} = iban;
    return {bankCode, branchCode, accountNumber};
  }

  const match = /^(\d{1,4})-(\d{1,4})-(\d{1,13})$/.exec(raw.replace(/\s+/g, ''));
  if (!match) return null;
  const [, bank = '', branch = '', account = ''] = match;
  return {
    bankCode: stripLeadingZeros(bank),
    branchCode: stripLeadingZeros(branch),
    accountNumber: stripLeadingZeros(account)
  };
}

function joinAccountParts(
  parts: Pick<IsraeliBankAccount, 'bankCode' | 'branchCode' | 'accountNumber'>
): string {
  return `${parts.bankCode}-${parts.branchCode}-${parts.accountNumber}`;
}
